import {useState, useEffect} from 'react'
import Modal from '../Modal/Modal.tsx'
import FormColumn from '@/components/ui/Form/components/FormColumn/FormColumn.tsx'
import FormLabel from '@/components/ui/Form/components/FormLabel/FormLabel.tsx'
import Input from '../../ui/Input/Input.tsx'
import PrefixInput from '@/components/ui/PrefixInput/PrefixInput.tsx'
import Button from '../../ui/Button/Button.tsx'
import css from './CreateWorkspaceModal.module.css'
import {useAppDispatch, useAppSelector} from '../../../store/hooks'
import {createWorkspaceSaga, clearCreateWorkspaceState} from '../../../store/workspace/workspaceSlice'
import {selectIsLoading} from '../../../store/ui/uiSelectors'
import IconClose from '../../../assets/icons/icon-close.svg?react'
import IconButton from '@/components/ui/IconButton/IconButton'
import {TextH2, TextNorm} from "../../ui/Text";
import IconLogo from '../../../assets/icons/img-logo.svg?react'
import FormError from "@/components/ui/Form/components/FormError/FormError.tsx";
import { workspaceNameError, WORKSPACE_NAME_MAX_LENGTH } from "@/utils/workspaceName";

interface CreateWorkspaceModalProps {
    onClose: () => void
    canClose: boolean
}

const CreateWorkspaceModal = ({onClose, canClose}: CreateWorkspaceModalProps) => {
    const dispatch = useAppDispatch()
    const isLoading = useAppSelector(selectIsLoading('create-workspace'))
    const error = useAppSelector(state => state.workspace.createWorkspace.error)

    const [name, setName] = useState('')
    const [workspaceUrl, setWorkspaceUrl] = useState('')
    const [urlManuallyEdited, setUrlManuallyEdited] = useState(false)

    useEffect(() => {
        return () => {
            dispatch(clearCreateWorkspaceState())
        }
    }, [dispatch])

    const toSlug = (value: string) =>
        value.toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-')

    // Only complain once something has been typed — an untouched empty field
    // shouldn't show an error; the submit button is already disabled for it.
    const nameError = name ? workspaceNameError(name) : null

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value
        setName(value)
        if (!urlManuallyEdited) {
            setWorkspaceUrl(toSlug(value))
        }
    }

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUrlManuallyEdited(true)
        setWorkspaceUrl(e.currentTarget.value)
    }

    const handleClose = () => {
        if (canClose) {
            onClose()
        }
    }

    const handleSubmit = () => {
        if (name && workspaceUrl && !nameError && !isLoading) {
            dispatch(createWorkspaceSaga({
                name,
                workspaceUrlId: workspaceUrl
            }))
        }
    }

    return (
        <Modal onClose={handleClose} maxWidth={416} canClose={canClose}>
            {canClose &&
                <div className={css.buttons}>
                    <IconButton icon={<IconClose />} onClick={onClose} />
                </div>
            }
            <div className={css.logo}>
                <IconLogo/>
            </div>
            <TextH2>Create a new workspace</TextH2>
            <div className={css.content}>
                <TextNorm>Workspace is a shared space where teams collaborate on projects and tasks.</TextNorm>
            </div>

            <FormColumn gap={16}>
                <FormColumn>
                    <FormLabel>Name</FormLabel>
                    <Input
                        placeholder="My Workspace"
                        value={name}
                        onChange={handleNameChange}
                        error={!!nameError}
                        maxLength={WORKSPACE_NAME_MAX_LENGTH}
                    />
                    {nameError && <FormError>{nameError}</FormError>}
                </FormColumn>
                <FormColumn>
                    <FormLabel>Workspace URL</FormLabel>
                    <PrefixInput
                        prefix={`${import.meta.env.VITE_APP_DOMAIN}/`}
                        placeholder="my-workspace"
                        value={workspaceUrl}
                        onChange={handleUrlChange}
                    />
                    {error && <FormError>{error}</FormError>}
                </FormColumn>
            </FormColumn>
            <div className={css.bottom_button}>
                <FormColumn align={'stretch'}>
                    <Button
                        title="Create workspace"
                        type="black"
                        size="medium"
                        onClick={handleSubmit}
                        loading={isLoading}
                        disabled={!name || !workspaceUrl || !!nameError}
                    />
                </FormColumn>
            </div>
        </Modal>
    );
}

export default CreateWorkspaceModal